import React, { memo, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import { toast } from "react-toastify";

import { CodeEditor } from "../../../../components/CMEditor/CodeEditor";
import { CustomModal } from "../../../../components/CustomModal";
import { InfoIcon, TrashIcon } from "../../../../components/icons";
import { defaultTools, toolsValidationSchema } from "../../constants";
import type { ToolType } from "../../types";

interface ToolsProps {
  selectedTool: ToolType | null;
  toolsFromStore: Record<string, ToolType>;
  handleClose: () => void;
  handleSaveTool: (tool: ToolType) => void;
  changeSelectedTool: (tool: ToolType | null) => void;
  handleDeleteToolFromStore: (
    e: React.MouseEvent<HTMLButtonElement>,
    id: number,
  ) => void;
}

const ToolsModal: React.FC<ToolsProps> = memo(
  ({
    selectedTool,
    toolsFromStore,
    handleClose,
    handleSaveTool,
    changeSelectedTool,
    handleDeleteToolFromStore,
  }) => {
    const [selectedExample, setSelectedExample] = useState<
      ToolType["tool"]["function"] | null
    >(null);

    const [value, setValue] = useState<ToolType["tool"]["function"] | "">(
      selectedTool?.tool?.function || "",
    );

    const [error, setError] = useState(false);

    const handleSave = () => {
      if (!value) {
        toast.error("Function definition is empty");
        return;
      }

      handleSaveTool({
        id: selectedTool?.id || Date.now(),
        tool: {
          type: "function",
          function: {
            ...value,
            strict: value.strict || false,
            parameters: {
              ...value.parameters,
              type: "object",
              properties: value.parameters?.properties || {},
              required: value.parameters?.required || [],
            },
          },
        },
      });
    };

    const handleChangeSelectedTool = (tool: ToolType) => {
      changeSelectedTool(tool);
      setError(false);
      setValue(tool.tool.function);
      setSelectedExample(tool.tool.function);
    };

    const handleSelectTool = (tool: ToolType["tool"]["function"]) => {
      setSelectedExample(tool);
      setError(false);
      setValue(tool);
    };

    const handleEditTool = (tool: any) => {
      setError(false);
      setValue(JSON.parse(tool));
    };

    const handleError = (error: boolean) => {
      setError(error);
    };

    const onCloseClick = () => {
      handleClose();
      changeSelectedTool(null);
    };

    return (
      <CustomModal
        title={selectedTool ? "Edit tool" : "Add tool"}
        show={true}
        handleClose={onCloseClick}
        size="lg"
        className="tools-modal"
        footer={
          <>
            <Button size="sm" variant="secondary" onClick={onCloseClick}>
              Close
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={handleSave}
              disabled={!value || error}
            >
              Save
            </Button>
          </>
        }
      >
        <p>
          The model will intelligently decide to call tools based on input it
          receives from the user.
        </p>
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mt-3">Default</h6>
          <Dropdown className="select-tool-dropdown">
            <Dropdown.Toggle
              variant="outline-primary"
              className="btn-select-tool"
              size="sm"
            >
              <span className="text-truncate">
                {selectedExample ? `${selectedExample.name}()` : "Examples"}
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Header>Default</Dropdown.Header>
              {defaultTools.map((tool, key) => (
                <Dropdown.Item key={key} onClick={() => handleSelectTool(tool)}>
                  <span>{tool.name}()</span>
                </Dropdown.Item>
              ))}
              <Dropdown.Header>Custom</Dropdown.Header>
              {Object.values(toolsFromStore).map(tool => (
                <Dropdown.Item
                  key={tool.id}
                  onClick={() => handleChangeSelectedTool(tool)}
                >
                  <span className="d-flex justify-content-between align-items-center w-100">
                    <span className="text-truncate">
                      {tool.tool.function.name}()
                    </span>
                    <Button
                      variant="light"
                      className="btn-tool-control"
                      onClick={e => handleDeleteToolFromStore(e, tool.id)}
                    >
                      <TrashIcon />
                    </Button>
                  </span>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="tools-editor-wrapper mt-4">
          <CodeEditor
            initialValue={selectedExample || selectedTool?.tool.function}
            onChange={handleEditTool}
            minHeight="340px"
            placeholder={JSON.stringify(defaultTools[0], null, 2)}
            lineNumbers={false}
            validationSchema={toolsValidationSchema}
            onError={handleError}
            autofocus
            withFooter
          />
        </div>
        <div className="tool-notification">
          <InfoIcon />
          Add
          <span className="tool-value">&#39;strict&#39;: true</span>
          to ensure the model&#39;s response always follows this schema.
        </div>
      </CustomModal>
    );
  },
);

export { ToolsModal };
